import { useMemo, useState, useRef } from "react";
import {
    MetaTagHeader,
    UnderlinedSectionHeader,
    Button,
    ScaleOnHoverDiv,
    LifeCycleAccordion,
    RequestDemo,
} from "@components";
import * as constants from "./constants";
import { Link } from "react-router-dom";
import News from "../../../data/products/template/components/News";
import SolutionHero from "../../template/components/Hero";
import Tabs from "../../../about/components/Team/components/Tabs";

export default function ProjectAnalysis() {
    const requestDemoRef = useRef(null);

    const scrollToRequestDemo = (e) => {
        e?.preventDefault();
        requestDemoRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    return (
        <>
            <MetaTagHeader {...constants.meta} />
            <main className="flex flex-col gap-24 bg-background pb-20 md:gap-32">
                <SolutionHero 
                    {...constants.hero} 
                    onButtonClick={scrollToRequestDemo}
                />
                <DifferenceSection
                    heading={constants.difference.heading}
                    description={constants.difference.description}
                    cards={constants.difference.cards}
                />
                <ParticipantsSection 
                    {...constants.participants} 
                    onCtaClick={scrollToRequestDemo}
                />
                <MethodologiesSection 
                    {...constants.methodologies} 
                />
                <LifeCycleAccordion {...constants.lifeCycle} />
                <div className="mx-auto flex w-full max-w-6xl justify-center px-6 md:px-10 lg:px-0 -mt-8 md:-mt-12">
                    <Button 
                        to={constants.methodologies.ctaLink} 
                        className="w-fit px-10"
                        onClick={scrollToRequestDemo}
                    >
                        {constants.methodologies.ctaText}
                    </Button>
                </div>
                <DataDeliverablesSection {...constants.dataDeliverables} />
                <div ref={requestDemoRef}>
                    <RequestDemoSection />
                </div>
                <NewsSection />
            </main>
        </>
    );
}


function DifferenceSection({ heading, description, cards }) {
    return (
        <section className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 md:px-10 lg:px-0">
            <UnderlinedSectionHeader className="mx-auto text-base md:text-[42px]">
                {heading}
            </UnderlinedSectionHeader>
            <p className="mx-auto max-w-3xl text-center font-body text-lg leading-8 text-darkGray md:text-xl md:leading-9">
                {description}
            </p>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3 mt-4">
                {cards?.map((card) => (
                    <DifferenceCard key={card.id} {...card} />
                ))}
            </div>
        </section>
    );
}

function DifferenceCard({ image, imageAlt, title, description, hoverDescription }) {
    return (
        <div className="flex flex-col h-full overflow-hidden rounded-lg cursor-pointer hover:scale-105 transition-transform duration-200 shadow-sm">
            <div className="flex h-64 items-center justify-center overflow-hidden">
                <img
                    src={image}
                    alt={imageAlt}
                    className="w-full h-full object-cover"
                    loading="lazy"
                />
            </div>
            <div className="flex flex-col bg-black text-white p-6 md:p-8 flex-1 relative min-h-[200px]">
                <h3 className="text-xl md:text-2xl font-bold mb-3">
                    {title}
                </h3>
                <div className="relative flex-1">
                    <p className="font-body text-base leading-7 text-white/90">
                        {description}
                    </p>
                </div>
            </div>
        </div>
    );
}

function ParticipantsSection({ heading, ctaText, ctaLink, items, onCtaClick }) {
    return (
        <section className="mx-auto flex w-full max-w-6xl flex-col items-center gap-10 px-6 md:px-10 lg:px-0">
            <UnderlinedSectionHeader className="mx-auto text-base md:text-[42px]">
                {heading}
            </UnderlinedSectionHeader>
            <div className="grid w-full gap-6 md:grid-cols-2 max-w-5xl mx-auto">
                {items.map((item) => (
                    <ScaleOnHoverDiv
                        key={item.title}
                        className="h-full"
                    >
                        <div className="flex h-full flex-col justify-between rounded-lg bg-blue px-8 py-10 text-black shadow-sm">
                            <div>
                                <h3 className="font-header text-xl md:text-2xl uppercase tracking-[0.25em]">
                                    {item.title}
                                </h3>
                                <p className="mt-4 font-body text-base leading-7">
                                    {item.body}
                                </p>
                            </div>
                            {item.ctaText && item.ctaLink && (
                                <Link
                                    to={item.ctaLink}
                                    className="mt-10 text-sm font-bold uppercase tracking-[0.35em] text-black"
                                >
                                    {item.ctaText}
                                </Link>
                            )}
                        </div>
                    </ScaleOnHoverDiv>
                ))}
            </div>
            <Button 
                to={ctaLink} 
                className="mx-auto w-fit px-10"
                onClick={onCtaClick}
            >
                {ctaText}
            </Button>
        </section>
    );
}

function MethodologiesSection({ heading, description, tabs }) {
    const [selectedTab, setSelectedTab] = useState(tabs[0]);
    const currentTab = useMemo(
        () => tabs.find((tab) => tab.id === selectedTab.id) ?? tabs[0],
        [selectedTab, tabs],
    );

    // Transform tabs to match Tabs component format (id and display)
    const transformedTabs = tabs.map((tab) => ({
        id: tab.id,
        display: tab.label,
    }));

    // Handle tab selection - Tabs component passes the full tab object
    const handleTabChange = (tab) => {
        const matchingTab = tabs.find((t) => t.id === tab.id);
        if (matchingTab) {
            setSelectedTab(matchingTab);
        }
    };

    const tabProps = {
        tabs: transformedTabs,
        selectedTab: selectedTab.id,
        setTab: handleTabChange,
        id: "methodology-tab-underline",
    };

    return (
        <section className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 md:px-10 lg:px-0">
            <UnderlinedSectionHeader className="mx-auto text-base md:text-[42px]">
                {heading}
            </UnderlinedSectionHeader>
            {description && (
                <p className="mx-auto max-w-3xl text-center font-body text-base leading-7 text-darkGray md:text-xl md:leading-9">
                    {description}
                </p>
            )}

            {/* Tabs - using Teams Tabs component */}
            <Tabs {...tabProps} />

            {/* Content - styled like ImgText component */}
            {/* DESKTOP */}
            <div className="hidden h-[600px] w-full items-center md:flex overflow-hidden rounded-lg">
                {/* Text Content - Left Side */}
                <div className="flex items-center justify-center h-full max-w-full md:w-1/2 bg-gray">
                    <div className="flex max-w-[600px] flex-col justify-center p-0 lg:p-10">
                        {currentTab?.body && (
                            <div className="solutions-content">
                                {typeof currentTab.body === "string" ? (
                                    <p className="text-base md:text-lg">{currentTab.body}</p>
                                ) : (
                                    <>
                                        {currentTab.body.title && (
                                            <p className="mb-4 font-bold text-2xl md:text-3xl">
                                                {currentTab.body.title}
                                            </p>
                                        )}
                                        {currentTab.body.text && (
                                            <p className="text-base md:text-lg">
                                                {currentTab.body.text}
                                            </p>
                                        )}
                                        {currentTab.body.intro && (
                                            <p className="mb-4 text-base md:text-lg">
                                                {currentTab.body.intro}
                                            </p>
                                        )}
                                        {currentTab.body.bullets && (
                                            <ul className="list-disc list-inside space-y-2 ml-4 text-base md:text-lg">
                                                {currentTab.body.bullets.map((bullet, index) => (
                                                    <li key={index}>{bullet}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {/* Image Content - Right Side */}
                <div className="flex h-full w-1/2 items-center justify-center">
                    <img
                        src={currentTab?.image}
                        alt={currentTab?.caption || currentTab?.label}
                        className="h-full w-full object-cover"
                        loading="lazy"
                    />
                </div>
            </div>

            {/* MOBILE */}
            <div className="flex w-full flex-col items-center px-10 py-10 md:hidden">
                <div className="my-8 flex h-full justify-center">
                    <img
                        src={currentTab?.image}
                        alt={currentTab?.caption || currentTab?.label}
                        className="h-full w-full object-cover"
                        loading="lazy"
                    />
                </div>
                {currentTab?.body && (
                    <div className="md:mt-4 space-y-4 break-words solutions-content">
                        {typeof currentTab.body === "string" ? (
                            <p className="text-base">{currentTab.body}</p>
                        ) : (
                            <>
                                {currentTab.body.title && (
                                    <p className="mb-4 font-bold text-2xl">
                                        {currentTab.body.title}
                                    </p>
                                )}
                                {currentTab.body.text && (
                                    <p className="text-base">
                                        {currentTab.body.text}
                                    </p>
                                )}
                                {currentTab.body.intro && (
                                    <p className="mb-4 text-base">
                                        {currentTab.body.intro}
                                    </p>
                                )}
                                {currentTab.body.bullets && (
                                    <ul className="list-disc list-inside space-y-2 ml-4 text-base">
                                        {currentTab.body.bullets.map((bullet, index) => (
                                            <li key={index}>{bullet}</li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}

function NewsSection() {
    return (
        <section className="w-full [&>div]:!mt-0">
            <News tag="blog" />
        </section>
    );
}

function DataDeliverablesSection({ heading, tableData }) {
    return (
        <section className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 md:px-10 lg:px-0">
            <UnderlinedSectionHeader className="mx-auto text-base md:text-[42px]">
                {heading}
            </UnderlinedSectionHeader>

            {/* Table */}
            {tableData && tableData.length > 0 && (
                <div className="overflow-x-auto mt-4 mx-auto max-w-5xl rounded-lg overflow-hidden shadow-sm">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-[#6B6B6B] text-white">
                                <th className="px-4 py-3 text-left font-extrabold">
                                    Dataset
                                </th>
                                <th className="px-4 py-3 text-left font-extrabold">
                                    Resolution
                                </th>
                                <th className="px-4 py-3 text-left font-extrabold">
                                    Geographic Extent
                                </th>
                                <th className="px-4 py-3 text-left font-extrabold">
                                    Temporal Coverage
                                </th>
                                <th className="px-4 py-3 text-left font-extrabold">
                                    Metrics
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.map((row, index) => (
                                <tr
                                    key={row.id}
                                    className={
                                        index % 2 === 0
                                            ? "bg-gray text-black"
                                            : "bg-white text-black"
                                    }
                                >
                                    <td className="px-4 py-3 font-body">
                                        {row.dataset}
                                    </td>
                                    <td className="px-4 py-3 font-body">
                                        {row.resolution}
                                    </td>
                                    <td className="px-4 py-3 font-body">
                                        {row.geographicExtent}
                                    </td>
                                    <td className="px-4 py-3 font-body">
                                        {row.temporalCoverage}
                                    </td>
                                    <td className="px-4 py-3 font-body">
                                        {row.metrics}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

function RequestDemoSection() {
    return (
        <section className="w-full [&>div]:!mb-0">
            <RequestDemo />
        </section>
    );
}